import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !name || !message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('name', name);
      formData.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d6ie9eo57726vudt88rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setName('');
        setMessage('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#1A1A2E] rounded-t-3xl relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-20 py-20">
        <div className="grid grid-cols-4 gap-12 relative z-10">
          {/* CTA Area */}
          <div className="col-span-1">
            <h3 className="text-white text-4xl font-bold leading-tight" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              一緒に<br />
              創りましょう
            </h3>
          </div>

          {/* Navigation Links */}
          <div className="col-span-1">
            <h4 className="text-white text-lg font-medium mb-6">メニュー</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  ホーム
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  会社概要
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  事業内容
                </button>
              </li>
              <li>
                <a href="#contact-form" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  お問い合わせ
                </a>
              </li>
            </ul>
          </div>

          {/* SNS Area */}
          <div className="col-span-1">
            <h4 className="text-white text-lg font-medium mb-6">フォローする</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-instagram-line text-base w-5 h-5 flex items-center justify-center"></i>
                  <span>Instagram</span>
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-facebook-fill text-base w-5 h-5 flex items-center justify-center"></i>
                  <span>Facebook</span>
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-twitter-x-line text-base w-5 h-5 flex items-center justify-center"></i>
                  <span>Twitter</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Form */}
          <div id="contact-form" className="col-span-1">
            <div className="mb-4">
              <p className="text-white text-base leading-relaxed">
                お気軽に<br />
                ご相談ください
              </p>
            </div>
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-3">
              <input
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="お名前"
                className="w-full bg-[#2A2A3E] text-white text-sm px-4 py-3 rounded-lg border border-gray-600 focus:border-[#FF8B6A] focus:outline-none transition-colors"
                required
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="メールアドレス"
                className="w-full bg-[#2A2A3E] text-white text-sm px-4 py-3 rounded-lg border border-gray-600 focus:border-[#FF8B6A] focus:outline-none transition-colors"
                required
              />
              <div className="relative">
                <textarea
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="メッセージ"
                  maxLength={500}
                  className="w-full bg-[#2A2A3E] text-white text-sm px-4 py-3 rounded-lg border border-gray-600 focus:border-[#FF8B6A] focus:outline-none transition-colors resize-none"
                  rows={3}
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="absolute bottom-3 right-3 w-10 h-10 flex items-center justify-center bg-white rounded-full hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-line text-[#1A1A2E] text-lg"></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-xs">送信が完了しました</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs">送信に失敗しました。もう一度お試しください。</p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Decoration */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
          <div className="text-white/5 font-black text-[200px] tracking-wider text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            OHISAMA
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-700 py-6">
        <div className="max-w-7xl mx-auto px-20">
          <p className="text-gray-500 text-xs text-center">
            © 2025 株式会社おひさま. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}